'use client';

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { ArrowUpRight } from '@phosphor-icons/react';
import type { PublicPromotion } from '@/lib/promotion-content';
import { useMoneyRules } from '@/lib/money-rules';
import './mobile-loyalty-pass.css';

interface MobileLoyaltyPassProps {
  offer: PublicPromotion;
  onOpen: (trigger: HTMLButtonElement, level: number) => void;
}

const formatNumber = (value: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);

/** A public preview of the published loyalty levels, independent of account status. */
export function MobileLoyaltyPass({ offer, onOpen }: MobileLoyaltyPassProps) {
  const rules = useMoneyRules();
  const levels = [...rules.loyalty.levels].sort(
    (a, b) => a.min_total - b.min_total || a.level - b.level
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = levels.find((level) => level.level === selectedId) ?? levels[levels.length - 1];
  const index = levels.findIndex((level) => level.level === selected?.level);
  const id = useId();
  const levelTrack = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = levelTrack.current;
    const active = track?.querySelector<HTMLInputElement>('input:checked')?.parentElement;
    if (!track || !active) return;
    // Keep the chosen option visible without scrolling the whole page on mount.
    const viewport = track.getBoundingClientRect();
    const option = active.getBoundingClientRect();
    if (option.left < viewport.left) track.scrollLeft -= viewport.left - option.left;
    else if (option.right > viewport.right) track.scrollLeft += option.right - viewport.right;
  }, [index, levels.length]);

  if (!selected) return null;

  const depth = levels.length > 1 ? index / (levels.length - 1) : 0;
  const rate = formatNumber(selected.earn_rate);
  const palette = {
    '--mlp-main': `hsl(267 58% ${48 - depth * 8}%)`,
    '--mlp-deep': `hsl(270 60% ${30 - depth * 4}%)`,
  } as CSSProperties;

  return (
    <section
      className="mobile-loyalty-pass"
      aria-labelledby={`${id}-title`}
      data-offer-slug={offer.slug}
    >
      <div className="mlp-content">
        <header className="mlp-heading">
          <p className="mlp-eyebrow">Коины за заказы</p>
          <h2 id={`${id}-title`}>{offer.title}</h2>
          <p className="mlp-description">{offer.description}</p>
        </header>

        <div className="mlp-card-wrap">
          <div className="mlp-card" style={palette} data-preview-level={selected.level}>
            <img
              className="mlp-coin-art"
              src="/storefront/offers-v2-loyalty.webp"
              alt=""
              width={1536}
              height={1024}
              loading="lazy"
              decoding="async"
              aria-hidden="true"
            />
            <div className="mlp-card-top" aria-hidden="true">
              <span className="mlp-brand" />
              <span className="mlp-edition">
                {String(index + 1).padStart(2, '0')}
                <span> / {String(levels.length).padStart(2, '0')}</span>
              </span>
            </div>
            <div id={`${id}-preview`} className="mlp-preview" aria-live="polite" aria-atomic="true">
              <p className="mlp-level-name">{selected.name}</p>
              <div className={`mlp-rate${rate.length > 2 ? ' mlp-rate--wide' : ''}`}>
                <strong>{rate}</strong>
                <span>%</span>
              </div>
              <p className="mlp-rate-caption">Возвращаем коинами</p>
              <div className="mlp-threshold">
                <p>
                  {selected.min_total === 0
                    ? 'С первого заказа'
                    : `От ${formatNumber(selected.min_total)}\u00a0₽ за всё время`}
                </p>
                <span>Учитываем доставленные заказы</span>
              </div>
            </div>
          </div>
        </div>

        <fieldset className="mlp-levels">
          <legend>Посмотрите, что даёт каждый уровень</legend>
          <div className="mlp-level-track" ref={levelTrack}>
            {levels.map((level, levelIndex) => (
              <label className="mlp-level-option" key={level.level}>
                <input
                  type="radio"
                  name={`${id}-level`}
                  value={level.level}
                  checked={index === levelIndex}
                  onChange={() => setSelectedId(level.level)}
                  onFocus={(event) =>
                    event.currentTarget.parentElement?.scrollIntoView({
                      block: 'nearest',
                      inline: 'nearest',
                      behavior: 'instant',
                    })
                  }
                  aria-controls={`${id}-preview`}
                />
                <span>{level.name}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <button className="mlp-open" onClick={(event) => onOpen(event.currentTarget, selected.level)}>
          <span>Как копить и тратить</span>
          <span className="mlp-open-arrow" aria-hidden="true">
            <ArrowUpRight size={21} weight="bold" />
          </span>
        </button>
      </div>
    </section>
  );
}
